<template>
    <v-card flat light class="upload mx-auto" :loading="loading">
        <v-card-title>{{ replacing ? $t('docs.upload.updTitle') : $t('docs.upload.title') }}</v-card-title>
        <v-card-subtitle>{{ replacing ? $t('docs.upload.updSubtitle') : $t('docs.upload.subtitle') }}</v-card-subtitle>
        <v-card-text v-if="maxUploadFilesCount || uploadIsDisabled" class="text-center">
            <div v-if="!replacing && maxUploadFilesCount">
                <span class="grey--text">{{ $t('docs.upload.maxFiles') }}: {{ maxUploadFilesCount }}</span>
            </div>
            <div v-if="maxUploadFileSize">
                <span class="grey--text">{{ $t('docs.upload.maxSize') }}: {{ formatBytes(maxUploadFileSize) }}</span>
            </div>
            <div v-if="uploadIsDisabled">
                <span class="red--text">{{ $t('docs.error.sameName') }}</span>
            </div>
        </v-card-text>
        <v-card-text v-if="listItems.length" class="pa-0 files-list-wrapper">
            <v-list two-line v-if="listItems.length">
                <v-list-item v-for="(file, index) in listImagedItems" :key="index" link>
                    <v-list-item-avatar>
                        <v-img v-if="file.preview" :src="file.preview"></v-img>
                        <v-icon v-else v-text="'mdi-file'" class="mdi-36px" color="grey lighten-1"></v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content>
                        <v-list-item-title v-text="file.name"></v-list-item-title>
                        <v-list-item-subtitle>{{ formatBytes(file.size) }} - {{ file.type }}</v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action>
                        <v-btn icon @click="remove(index)">
                            <v-icon color="grey lighten-1">mdi-close</v-icon>
                        </v-btn>
                    </v-list-item-action>
                </v-list-item>
            </v-list>
        </v-card-text>
        <v-card-text v-else class="text-center" @click.stop="$refs.inputUpload.click()" style="cursor: pointer">
            <v-file-input disabled :label="$t('docs.upload.selectAFile')" />
        </v-card-text>
        <v-divider></v-divider>
        <input ref="inputUpload" type="file" multiple v-show="false" @change="add" />
        <v-toolbar dense flat>
            <div class="grow"></div>
            <v-btn text @click="$emit('cancel')" class="upload__button mx-1">{{ $t('docs.upload.cancel') }}</v-btn>
            <v-btn depressed @click="listItems = []" class="upload__button mx-1" :disabled="!listItems.length">
                <v-icon>mdi-close</v-icon>{{ $t('docs.upload.clear') }}
            </v-btn>
            <v-btn
                :disabled="addIsDisabled"
                depressed
                @click="$refs.inputUpload.click()"
                class="upload__button upload__button--reverse mx-1"
            >
                <v-icon left>mdi-plus-circle</v-icon>{{ $t('docs.upload.add') }}
            </v-btn>
            <v-btn
                depressed
                @click="upload"
                class="upload__button upload__button--reverse ml-1"
                :disabled="!listItems.length || uploadIsDisabled"
            >
                {{ $t('docs.upload.upload') }}
                <v-icon right>mdi-upload-outline</v-icon>
            </v-btn>
        </v-toolbar>
        <v-overlay :value="uploading" :absolute="true" color="white" opacity="0.9">
            <v-progress-linear v-model="progress" height="25" striped rounded reactive>
                <strong>{{ Math.ceil(progress) }}%</strong>
            </v-progress-linear>
        </v-overlay>
    </v-card>
</template>

<script>
import { formatBytes } from './util';
import { api } from '@/utils/api';

const imageMimeTypes = ['image/png', 'image/jpeg'];

export default {
    props: {
        path: String,
        client: String,
        driveItemId: String,
        storage: String,
        maxUploadFilesCount: { type: Number, default: 0 },
        maxUploadFileSize: { type: Number, default: 0 },
        replacing: { type: Boolean, default: false },
        nameToMatch: { type: String, default: '' }
    },
    data() {
        return {
            loading: false,
            uploading: false,
            progress: 0,
            listItems: [],
            listImagedItems: []
        };
    },
    methods: {
        formatBytes,
        async filesMap() {
            const promises = this.listItems.map(file => {
                const result = {
                    name: file.name,
                    type: file.type,
                    size: file.size,
                    extension: file.name.split('.').pop()
                };
                return new Promise(resolve => {
                    if (!imageMimeTypes.includes(result.type)) {
                        return resolve(result);
                    }
                    const reader = new FileReader();
                    reader.onload = function (e) {
                        result.preview = e.target.result;
                        resolve(result);
                    };
                    reader.readAsDataURL(file);
                });
            });
            this.loading = true;
            this.listImagedItems = await Promise.all(promises);
            this.loading = false;
        },

        async add(event) {
            const files = Array.from(event.target.files);
            this.listItems = [...this.listItems, ...files];
            this.$refs.inputUpload.value = '';
        },

        remove(index) {
            this.listItems = this.listItems.length === 1 ? [] : this.listItems.filter((i, j) => j !== index && i);
        },

        async upload() {
            const formData = new FormData();

            // files
            for (const file of this.listItems) {
                formData.append('files', file, file.name);
            }

            formData.append('path', this.path);
            formData.append('client', this.client);
            formData.append('driveItemId', this.driveItemId);

            const config = {
                onUploadProgress: progressEvent => {
                    this.progress = (progressEvent.loaded / progressEvent.total) * 100;
                }
            };
            formData.append('clientId', this.$store.getters.getCurrentClientId);
            formData.append('portletId', this.$store.getters.getCurrentDocsPortlet);
            this.uploading = true;
            try {
                const data = await api.post(['sharepoint', 'upload'], null, formData, config);
                this.$emit('errored', data?.join(', '));
            } catch (error) {
                console.log('file upload error');
                this.$store.commit('displayErrors', { errno: 4405 });
            } finally {
                this.$emit('uploaded');
                this.listItems = [];
                this.uploading = false;
            }
        }
    },
    computed: {
        addIsDisabled() {
            return !!this.maxUploadFilesCount && this.listItems.length >= this.maxUploadFilesCount;
        },
        uploadIsDisabled() {
            return this.replacing && !!this.listItems.length && this.listItems[0].name !== this.nameToMatch;
        }
    },
    watch: {
        listItems(list) {
            this.filesMap();
        }
    }
};
</script>

<style lang="scss" scoped>
::v-deep .v-overlay__content {
    width: 90%;
    max-width: 500px;

    .files-list-wrapper {
        max-height: 250px;
        overflow-y: hidden;

        div {
            padding-top: 0;
        }
    }
}

.upload {
    &__button {
        margin: 6px;
        font-family: 'IBM Plex Sans', monospace;
        font-size: 14px;
        text-transform: none;
        text-indent: 0;
        letter-spacing: 0;
        font-weight: bold;
        padding: 0 16px !important;
        &--reverse {
            background-color: #3200e6 !important;
            color: white;
        }
    }
}
</style>
